/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import ActionCreator from '../action-creator';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../components/common/map/GoogleMapSingleton';
import { ToggleTileStateEnum } from '../../components/materialui_wrappers/ToggleTile';
import { MapSizesEnum } from '../../components/common/map_settings/MapSizeController';
import { BeamOnMap, TailTagSize } from './reducer';
import GroundStation from '../../shared/entities/groundStation';
import { LegInfo } from '../../components/common/map_overlays/RulerToolDistanceSummary';

// TODO: make each of these unique when the maps are seperated into their own components
export const FTV_MAP_ID = GOOGLE_MAP_INSTANCE_KEY;
export const FLEET_MAP_ID = GOOGLE_MAP_INSTANCE_KEY;
export const PERFORMANCE_OUTLOOK_MAP_ID = GOOGLE_MAP_INSTANCE_KEY;
export const TICKETS_PAGE_MAP_ID = GOOGLE_MAP_INSTANCE_KEY;
export const DATA_USAGE_PAGE_MAP_ID = GOOGLE_MAP_INSTANCE_KEY;
export const PROVISIONING_PAGE_MAP_ID = GOOGLE_MAP_INSTANCE_KEY;
export const MANUAL_DOWNTIME_MAP_ID = GOOGLE_MAP_INSTANCE_KEY;
export const TRANSPORT_MONITORING_MAP_ID = GOOGLE_MAP_INSTANCE_KEY + 'TransportMonitoring';
export const FULL_SPACE_VEHICLE_VIEW_MAP_ID = GOOGLE_MAP_INSTANCE_KEY + 'FullSpaceVehicleView';

export interface MapIdProp {
  mapId: string;
}

interface SetMapDimensionsProps extends MapIdProp {
  width: string;
  height: string;
}

interface SetMapPositionProps extends MapIdProp {
  top: number;
  left: number;
}

interface SetMissionPlanMarkerProps extends MapIdProp {
  marker: google.maps.Marker;
}

interface SetMissionPlanPathMarkersProps extends MapIdProp {
  markers: google.maps.Marker[];
}

interface TileStateProps extends MapIdProp {
  tileState: ToggleTileStateEnum;
}

interface FtvMapSizeProps extends MapIdProp {
  size: MapSizesEnum;
  height: number;
}

interface ShowMapRulerToolProps extends MapIdProp {
  followAircraft: boolean;
}

interface LatLngRulerProps extends MapIdProp {
  latLng: google.maps.LatLng;
}

interface UpdateLatLngRulerProps extends LatLngRulerProps {
  index: number;
}

interface RemoveLatLngRulerProps extends MapIdProp {
  index: number;
}

interface SetRulerLegDataProps extends MapIdProp {
  legData: LegInfo[];
}

interface SetMarkersAndPolylinesRulerProps extends MapIdProp {
  markersAndPolylines: { markers: google.maps.Marker[]; polylines: google.maps.Polyline[] };
}

interface SetAntennaTypeIdProps extends MapIdProp {
  antennaTypeId: number | undefined;
}

interface SetHnaZonesCheckedProps extends MapIdProp {
  checked: boolean;
}

interface HubsListProps extends MapIdProp {
  hubs: string[];
}

interface GroundStationsListProps extends MapIdProp {
  groundStations: GroundStation[];
}

export interface BeamProps extends MapIdProp {
  beamId: string;
  networkType: string;
  mapType: string
}

interface TailTagSizeProps extends MapIdProp {
  tailTagSize: TailTagSize;
}

interface ShownProps extends MapIdProp {
  shown: boolean;
}

interface UpdateContextProps extends MapIdProp {
  geojson: any;
}

interface ExpansionPanelIdProps extends MapIdProp {
  openedExpansionPanelId: string | null;
}

interface CurrentBeamsProps extends MapIdProp {
  beamsOnMap: BeamOnMap[];
}

interface NextBeamProps extends MapIdProp {
  nextBeam: BeamOnMap;
}

interface CheckedProps extends MapIdProp {
  checked: boolean;
}
interface DisabledProps extends MapIdProp {
  disabled: boolean;
}

interface LatLngProps extends MapIdProp {
  latLng: google.maps.LatLng | google.maps.LatLngLiteral | undefined;
}
interface ZoomProps extends MapIdProp {
  center?: google.maps.LatLng;
  zoom?: number;
}

interface HiddenProps extends MapIdProp {
  hidden: boolean;
}

export const ActionCreators = {
  // Map Dimensions
  SetMapDimensions: new ActionCreator<'SetMapDimensions', SetMapDimensionsProps>('SetMapDimensions'),
  ClearMapDimensions: new ActionCreator<'ClearMapDimensions', MapIdProp>('ClearMapDimensions'),

  // Map Position
  SetMapPosition: new ActionCreator<'SetMapPosition', SetMapPositionProps>('SetMapPosition'),
  ClearMapPosition: new ActionCreator<'ClearMapPosition', any>('ClearMapPosition'),

  // Map Markers
  SetMissionPlanTakeoffMarker: new ActionCreator<'SetMissionPlanTakeoffMarker', SetMissionPlanMarkerProps>(
    'SetMissionPlanTakeoffMarker',
  ),
  SetMissionPlanLandingMarker: new ActionCreator<'SetMissionPlanLandingMarker', SetMissionPlanMarkerProps>(
    'SetMissionPlanLandingMarker',
  ),
  SetMissionPlanPathMarkers: new ActionCreator<'SetMissionPlanPathMarkers', SetMissionPlanPathMarkersProps>(
    'SetMissionPlanPathMarkers',
  ),

  // Map Settings Panel
  ToggleMapSettingsPanel: new ActionCreator<'ToggleMapSettingsPanel', MapIdProp>('ToggleMapSettingsPanel'),
  ShowMapSettingsPanel: new ActionCreator<'ShowMapSettingsPanel', MapIdProp>('ShowMapSettingsPanel'),
  HideMapSettingsPanel: new ActionCreator<'HideMapSettingsPanel', MapIdProp>('HideMapSettingsPanel'),
  SetMapSettingsSatView: new ActionCreator<'SetMapSettingsSatView', TileStateProps>('SetMapSettingsSatView'),
  SetMapSettingsLabels: new ActionCreator<'SetMapSettingsLabels', TileStateProps>('SetMapSettingsLabels'),
  SetMapSettingsPrecipRadar: new ActionCreator<'SetMapSettingsPrecipRadar', TileStateProps>(
    'SetMapSettingsPrecipRadar',
  ),
  SetMapSettingsFTVMapSize: new ActionCreator<'SetMapSettingsFTVMapSize', FtvMapSizeProps>('SetMapSettingsFTVMapSize'),

  // Performance Outlook Legend Panel
  TogglePerformanceOutlookLegendPanel: new ActionCreator<'TogglePerformanceOutlookLegendPanel', MapIdProp>(
    'TogglePerformanceOutlookLegendPanel',
  ),
  ShowPerformanceOutlookLegendPanel: new ActionCreator<'PerformanceOutlookLegendPanel', MapIdProp>(
    'PerformanceOutlookLegendPanel',
  ),
  HidePerformanceOutlookLegendPanel: new ActionCreator<'HidePerformanceOutlookLegendPanel', MapIdProp>(
    'HidePerformanceOutlookLegendPanel',
  ),

  // Map Overlays Panel
  ToggleOverlaysPanel: new ActionCreator<'ToggleOverlaysPanel', MapIdProp>('ToggleOverlaysPanel'),
  ShowOverlaysPanel: new ActionCreator<'ShowOverlaysPanel', MapIdProp>('ShowOverlaysPanel'),
  HideOverlaysPanel: new ActionCreator<'HideOverlaysPanel', MapIdProp>('HideOverlaysPanel'),

  // Map Tail Tag Overlays Panel
  ToggleTailTagOverlaysPanel: new ActionCreator<'ToggleTailTagOverlaysPanel', MapIdProp>('ToggleTailTagOverlaysPanel'),
  ShowTailTagOverlaysPanel: new ActionCreator<'ShowTailTagOverlaysPanel', MapIdProp>('ShowTailTagOverlaysPanel'),
  HideTailTagOverlaysPanel: new ActionCreator<'HideTailTagOverlaysPanel', MapIdProp>('HideTailTagOverlaysPanel'),

  // Map Ruler Tool Actions
  ToggleMapRulerTool: new ActionCreator<'ToggleMapRulerTool', ShowMapRulerToolProps>('ToggleMapRulerTool'),
  ShowMapRulerTool: new ActionCreator<'ShowMapRulerTool', ShowMapRulerToolProps>('ShowMapRulerTool'),
  HideMapRulerTool: new ActionCreator<'HideMapRulerTool', MapIdProp>('HideMapRulerTool'),
  AppendLatLngToMapRulerTool: new ActionCreator<'AppendLatLngToMapRulerTool', LatLngRulerProps>(
    'AppendLatLngToMapRulerTool',
  ),
  PrependLatLngToMapRulerTool: new ActionCreator<'PrependLatLngToMapRulerTool', LatLngRulerProps>(
    'PrependLatLngToMapRulerTool',
  ),
  UpdateLatLngInMapRulerTool: new ActionCreator<'UpdateLatLngInMapRulerTool', UpdateLatLngRulerProps>(
    'UpdateLatLngInMapRulerTool',
  ),
  RemoveLatLngInMapRulerTool: new ActionCreator<'RemoveLatLngInMapRulerTool', RemoveLatLngRulerProps>(
    'RemoveLatLngInMapRulerTool',
  ),
  ClearLatLngsInMapRulerTool: new ActionCreator<'ClearLatLngsInMapRulerTool', MapIdProp>('ClearLatLngsInMapRulerTool'),
  SetLegDataMapRulerTool: new ActionCreator<'SetLegDataMapRulerTool', SetRulerLegDataProps>('SetLegDataMapRulerTool'),
  ClearLegDataMapRulerTool: new ActionCreator<'ClearLegDataMapRulerTool', MapIdProp>('ClearLegDataMapRulerTool'),
  SetMarkersAndPolylinesMapRulerTool: new ActionCreator<
    'SetMarkersAndPolylinesMapRulerTool',
    SetMarkersAndPolylinesRulerProps
  >('SetMarkersAndPolylinesMapRulerTool'),

  // Overlays Ui States
  SetSelectedAntennaTypeId: new ActionCreator<'SetSelectedAntennaTypeId', SetAntennaTypeIdProps>(
    'SetSelectedAntennaTypeId',
  ),
  SetKuHnaZonesChecked: new ActionCreator<'SetKuHnaZonesChecked', SetHnaZonesCheckedProps>('SetKuHnaZonesChecked'),
  SetKaHnaZonesChecked: new ActionCreator<'SetKaHnaZonesChecked', SetHnaZonesCheckedProps>('SetKaHnaZonesChecked'),
  SetHcxHnaZonesChecked: new ActionCreator<'SetHcxHnaZonesChecked', SetHnaZonesCheckedProps>('SetHcxHnaZonesChecked'),
  SetGxHnaZonesChecked: new ActionCreator<'SetGxHnaZonesChecked', SetHnaZonesCheckedProps>('SetGxHnaZonesChecked'),
  SwitchToRlContours: new ActionCreator<'SwitchToRlContours', MapIdProp>('SwitchToRlContours'),
  SwitchToFlContours: new ActionCreator<'SwitchToFlContours', MapIdProp>('SwitchToFlContours'),
  EnableHeatMaps: new ActionCreator<'EnableHeatMaps', MapIdProp>('EnableHeatMaps'),
  DisableHeatMaps: new ActionCreator<'DisableHeatMaps', MapIdProp>('DisableHeatMaps'),
  EnableGroundStations: new ActionCreator<'EnableGroundStations', MapIdProp>('EnableGroundStations'),
  DisableGroundStations: new ActionCreator<'DisableGroundStations', MapIdProp>('DisableGroundStations'),
  AddHubsToMap: new ActionCreator<'AddHubsToMap', HubsListProps>('AddHubsToMap'),
  SetHubsOnMap: new ActionCreator<'SetHubsOnMap', HubsListProps>('SetHubsOnMap'),
  ClearHubsOnMap: new ActionCreator<'ClearHubsOnMap', any>('ClearHubsOnMap'),
  AddGroundStationsToMap: new ActionCreator<'AddGroundStationsToMap', GroundStationsListProps>(
    'AddGroundStationsToMap',
  ),
  SetGroundStationsOnMap: new ActionCreator<'SetGroundStationsOnMap', GroundStationsListProps>(
    'SetGroundStationsOnMap',
  ),
  ClearGroundStationsOnMap: new ActionCreator<'ClearGroundStationsOnMap', any>('ClearGroundStationsOnMap'),
  AddBeamToMap: new ActionCreator<'AddBeamToMap', BeamProps>('AddBeamToMap'),
  RemoveBeamFromMap: new ActionCreator<'RemoveBeamFromMap', BeamProps>('RemoveBeamFromMap'),
  ClearBeamsOnMap: new ActionCreator<'ClearBeamsOnMap', MapIdProp>('ClearBeamsOnMap'),
  UpdateCurrentOverlaysContext: new ActionCreator<'UpdateCurrentOverlaysContext', UpdateContextProps>(
    'UpdateCurrentOverlaysContext',
  ),
  SetOpenedExpansionPanelId: new ActionCreator<'SetOpenedExpansionPanelId', ExpansionPanelIdProps>(
    'SetOpenedExpansionPanelId',
  ),
  SetCurrentBeams: new ActionCreator<'SetCurrentBeams', CurrentBeamsProps>('SetCurrentBeams'),
  SetNextBeam: new ActionCreator<'SetNextBeam', NextBeamProps>('SetNextBeam'),
  SetWeatherOverlayShown: new ActionCreator<'SetWeatherOverlayShown', ShownProps>('SetWeatherOverlayShown'),

  // TAIL TAG ACTIONS
  ClearCurrentOverlaysContextExceptTailTagOptions: new ActionCreator<
    'ClearCurrentOverlaysContextExceptTailTagOptions',
    any
  >('ClearCurrentOverlaysContextExceptTailTagOptions'),
  SetTailTagsShown: new ActionCreator<'SetTailTagsShown', ShownProps>('SetTailTagsShown'),
  SetTerminalTagsSize: new ActionCreator<'SetTerminalTagsSize', TailTagSizeProps>('SetTerminalTagsSize'),
  SaveTerminalTagStateSuccessful: new ActionCreator<'SaveTerminalTagStateSuccessful', undefined>(
    'SaveTerminalTagStateSuccessful',
  ),
  SaveTerminalTagStateError: new ActionCreator<'SaveTerminalTagStateError', undefined>('SaveTerminalTagStateError'),

  // Mission Overlay Actions
  ClearMission: new ActionCreator<'ClearMission', MapIdProp>('ClearMission'),
  ToggleOverlaidFlightLegVisibility: new ActionCreator<'ToggleOverlaidFlightLegVisibility', MapIdProp>(
    'ToggleOverlaidFlightLegVisibility',
  ),
  ShowOverlaidFlightLeg: new ActionCreator<'ShowOverlaidFlightLeg', MapIdProp>('ShowOverlaidFlightLeg'),
  SetFlightPathBeamsDisabled: new ActionCreator<'SetFlightPathBeamsDisabled', DisabledProps>(
    'SetFlightPathBeamsDisabled',
  ),
  SetFlightPathBeamsChecked: new ActionCreator<'SetFlightPathBeamsChecked', CheckedProps>('SetFlightPathBeamsChecked'),
  SetFlightPathWaypointsDisabled: new ActionCreator<'SetFlightPathWaypointsDisabled', DisabledProps>(
    'SetFlightPathWaypointsDisabled',
  ),
  SetFlightPathWaypointsChecked: new ActionCreator<'SetFlightPathWaypointsChecked', CheckedProps>(
    'SetFlightPathWaypointsChecked',
  ),
  OverlaidFlightLegHidden: new ActionCreator<'OverlaidFlightLegHidden', MapIdProp>('OverlaidFlightLegHidden'),

  SetCurrentNextBeamsChecked: new ActionCreator<'SetCurrentNextBeamsChecked', CheckedProps>(
    'SetCurrentNextBeamsChecked',
  ),
  SetCurrentNextBeamsDisabled: new ActionCreator<'SetCurrentNextBeamsDisabled', DisabledProps>(
    'SetCurrentNextBeamsDisabled',
  ),

  SetZoomLatLng: new ActionCreator<'SetZoomLatLng', LatLngProps>('SetZoomLatLng'),
  SetSavedZoomSettings: new ActionCreator<'SetSavedZoomSettings', ZoomProps>('SetSavedZoomSettings'),

  SetMapHidden: new ActionCreator<'SetMapHidden', HiddenProps>('SetMapHidden'),

  // GPS SA Actions
  SetGpsSaMapLayersChecked: new ActionCreator<'SetGpsSaMapLayersChecked', CheckedProps>('SetGpsSaMapLayersChecked'),
  SetGpsSaMapLayersLowAccuracyChecked: new ActionCreator<'SetGpsSaMapLayersLowAccuracyChecked', CheckedProps>(
    'SetGpsSaMapLayersLowAccuracyChecked',
  ),
  SetGpsSaMapLayersReducedAccuracyChecked: new ActionCreator<'SetGpsSaMapLayersReducedAccuracyChecked', CheckedProps>(
    'SetGpsSaMapLayersReducedAccuracyChecked',
  ),
  SetGpsSaMapLayersNormalAccuracyChecked: new ActionCreator<'SetGpsSaMapLayersNormalAccuracyChecked', CheckedProps>(
    'SetGpsSaMapLayersNormalAccuracyChecked',
  ),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

// Map Dimensions
export function setMapDimensions(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (width: string, height: string) => {
    return ActionCreators.SetMapDimensions.create({ width: width, height: height, mapId: mapId });
  };
}

export function clearMapDimensions(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearMapDimensions.create({ mapId: mapId });
  };
}

// Map Position
export function setMapPosition(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (top: number, left: number) => {
    return ActionCreators.SetMapPosition.create({ top: top, left: left, mapId: mapId });
  };
}

export function clearMapPosition(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearMapPosition.create({ mapId: mapId });
  };
}

// Map Markers
export function setMissionPlanTakeoffMarker(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (marker: google.maps.Marker) => {
    return ActionCreators.SetMissionPlanTakeoffMarker.create({ marker: marker, mapId: mapId });
  };
}

export function setMissionPlanLandingMarker(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (marker: google.maps.Marker) => {
    return ActionCreators.SetMissionPlanLandingMarker.create({ marker: marker, mapId: mapId });
  };
}

export function setMissionPlanPathMarkers(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (markers: google.maps.Marker[]) => {
    return ActionCreators.SetMissionPlanPathMarkers.create({ markers: markers, mapId: mapId });
  };
}

// Map Settings Panel
export function showMapSettingsPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ShowMapSettingsPanel.create({ mapId: mapId });
  };
}

export function hideMapSettingsPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.HideMapSettingsPanel.create({ mapId: mapId });
  };
}

export function toggleMapSettingsPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ToggleMapSettingsPanel.create({ mapId: mapId });
  };
}

export function setMapSettingsSatView(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (toggleTileState: ToggleTileStateEnum) => {
    return ActionCreators.SetMapSettingsSatView.create({ tileState: toggleTileState, mapId: mapId });
  };
}

export function setMapSettingsLabels(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (toggleTileState: ToggleTileStateEnum) => {
    return ActionCreators.SetMapSettingsLabels.create({ tileState: toggleTileState, mapId: mapId });
  };
}

export function setMapSettingsPrecipRadar(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (toggleTileState: ToggleTileStateEnum) => {
    return ActionCreators.SetMapSettingsPrecipRadar.create({ tileState: toggleTileState, mapId: mapId });
  };
}

export function setMapSettingsFTVMapSize(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (mapSize: MapSizesEnum, mapHeight: number) => {
    return ActionCreators.SetMapSettingsFTVMapSize.create({ size: mapSize, height: mapHeight, mapId: mapId });
  };
}
// Performance Outlook Legend Panel
export function showTogglePerformanceOutlookLegendPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ShowPerformanceOutlookLegendPanel.create({ mapId: mapId });
  };
}

export function hidePerformanceOutlookLegendPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.HidePerformanceOutlookLegendPanel.create({ mapId: mapId });
  };
}

export function togglePerformanceOutlookLegendPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.TogglePerformanceOutlookLegendPanel.create({ mapId: mapId });
  };
}

// Map Overlays Panel
export function showOverlaysPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ShowOverlaysPanel.create({ mapId: mapId });
  };
}

export function hideOverlaysPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.HideOverlaysPanel.create({ mapId: mapId });
  };
}

export function toggleOverlaysPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ToggleOverlaysPanel.create({ mapId: mapId });
  };
}

// Map Tail Tag Overlays Panel
export function toggleTailTagOverlaysPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ToggleTailTagOverlaysPanel.create({ mapId: mapId });
  };
}

export function showTailTagOverlaysPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ShowTailTagOverlaysPanel.create({ mapId: mapId });
  };
}

export function hideTailTagOverlaysPanel(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.HideTailTagOverlaysPanel.create({ mapId: mapId });
  };
}

// Map Ruler Tool
export function toggleMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (followAircraft = false) => {
    return ActionCreators.ToggleMapRulerTool.create({ mapId: mapId, followAircraft: followAircraft });
  };
}

export function showMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (followAircraft = false) => {
    return ActionCreators.ShowMapRulerTool.create({ mapId: mapId, followAircraft: followAircraft });
  };
}

export function hideMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.HideMapRulerTool.create({ mapId: mapId });
  };
}

export function appendLatLngToMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (latLng: google.maps.LatLng) => {
    return ActionCreators.AppendLatLngToMapRulerTool.create({ latLng: latLng, mapId: mapId });
  };
}

export function prependLatLngToMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (latLng: google.maps.LatLng) => {
    return ActionCreators.PrependLatLngToMapRulerTool.create({ latLng: latLng, mapId: mapId });
  };
}

export function updateLatLngInMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (latLng: google.maps.LatLng, index: number) => {
    return ActionCreators.UpdateLatLngInMapRulerTool.create({ latLng: latLng, index: index, mapId: mapId });
  };
}

export function removeLatLngInMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (index: number) => {
    return ActionCreators.RemoveLatLngInMapRulerTool.create({ index: index, mapId: mapId });
  };
}

export function clearLatLngsInMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearLatLngsInMapRulerTool.create({ mapId: mapId });
  };
}

export function setLegDataMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (legData: LegInfo[]) => {
    return ActionCreators.SetLegDataMapRulerTool.create({ legData: legData, mapId: mapId });
  };
}

export function clearLegDataMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearLegDataMapRulerTool.create({ mapId: mapId });
  };
}

export function setMarkersAndPolylinesMapRulerTool(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (markersAndPolylines: { markers: google.maps.Marker[]; polylines: google.maps.Polyline[] }) => {
    return ActionCreators.SetMarkersAndPolylinesMapRulerTool.create({ mapId: mapId, markersAndPolylines });
  };
}

// Overlays Ui State
export function setSelectedAntennaTypeId(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (antennaTypeId: number | undefined) => {
    return ActionCreators.SetSelectedAntennaTypeId.create({ antennaTypeId: antennaTypeId, mapId: mapId });
  };
}

export function setKuHnaZonesChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetKuHnaZonesChecked.create({ checked: checked, mapId: mapId });
  };
}
export function setKaHnaZonesChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetKaHnaZonesChecked.create({ checked: checked, mapId: mapId });
  };
}
export function setHcxHnaZonesChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetHcxHnaZonesChecked.create({ checked: checked, mapId: mapId });
  };
}
export function setGxHnaZonesChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetGxHnaZonesChecked.create({ checked: checked, mapId: mapId });
  };
}
export function switchToFlContours(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.SwitchToFlContours.create({ mapId: mapId });
  };
}

export function switchToRlContours(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.SwitchToRlContours.create({ mapId: mapId });
  };
}

export function enableHeatMaps(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.EnableHeatMaps.create({ mapId: mapId });
  };
}

export function disableHeatMaps(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.DisableHeatMaps.create({ mapId: mapId });
  };
}

export function enableGroundStations(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.EnableGroundStations.create({ mapId: mapId });
  };
}

export function disableGroundStations(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.DisableGroundStations.create({ mapId: mapId });
  };
}

export function addBeamToMap(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (beamId: string, networkType: string, mapType: string) => {
    return ActionCreators.AddBeamToMap.create({ beamId, networkType, mapType, mapId });
  };
}

export function addGroundStationsToMap(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (groundStations: GroundStation[]) => {
    return ActionCreators.AddGroundStationsToMap.create({ groundStations, mapId });
  };
}

export function clearGroundStationsOnMap(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearGroundStationsOnMap.create({ mapId });
  };
}

export function removeBeamFromMap(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (beamId: string, networkType: string, mapType: string) => {
    return ActionCreators.RemoveBeamFromMap.create({ beamId, networkType, mapType, mapId });
  };
}

export function clearBeamsOnMap(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearBeamsOnMap.create({ mapId: mapId });
  };
}

export function updateCurrentOverlaysContext(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (geojson: any) => {
    return ActionCreators.UpdateCurrentOverlaysContext.create({ geojson, mapId });
  };
}

export function setOpenedExpansionPanelId(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (openedExpansionPanelId: string | null) => {
    return ActionCreators.SetOpenedExpansionPanelId.create({ openedExpansionPanelId, mapId });
  };
}

export function setCurrentBeams(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (beamsOnMap: BeamOnMap[]) => {
    return ActionCreators.SetCurrentBeams.create({ beamsOnMap, mapId });
  };
}

export function setNextBeam(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (nextBeam: BeamOnMap) => {
    return ActionCreators.SetNextBeam.create({ nextBeam, mapId });
  };
}

export function setWeatherOverlayShown(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (shown: boolean) => {
    return ActionCreators.SetWeatherOverlayShown.create({ shown, mapId });
  };
}

// Terminal Tag
export function clearCurrentOverlaysContextExceptTailTagOptions(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearCurrentOverlaysContextExceptTailTagOptions.create({ mapId: mapId });
  };
}
export function setTailTagsShown(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (shown: boolean) => {
    return ActionCreators.SetTailTagsShown.create({ shown, mapId });
  };
}
export function setTerminalTagsSize(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (tailTagSize: TailTagSize) => {
    return ActionCreators.SetTerminalTagsSize.create({ tailTagSize, mapId });
  };
}

export function clearMission(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ClearMission.create({ mapId });
  };
}

export function showOverlaidFlightLeg(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ShowOverlaidFlightLeg.create({ mapId });
  };
}

export function toggleOverlaidFlightLegVisibility(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.ToggleOverlaidFlightLegVisibility.create({ mapId });
  };
}

export function setFlightPathBeamsDisabled(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (disabled: boolean) => {
    return ActionCreators.SetFlightPathBeamsDisabled.create({ disabled, mapId });
  };
}
export function setFlightPathBeamsChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetFlightPathBeamsChecked.create({ checked, mapId });
  };
}
export function setFlightPathWaypointsDisabled(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (disabled: boolean) => {
    return ActionCreators.SetFlightPathWaypointsDisabled.create({ disabled, mapId });
  };
}
export function setFlightPathWaypointsChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetFlightPathWaypointsChecked.create({ checked, mapId });
  };
}
export function overlaidFlightLegHidden(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.OverlaidFlightLegHidden.create({ mapId });
  };
}

export function setCurrentNextBeamsChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetCurrentNextBeamsChecked.create({ checked, mapId });
  };
}
export function setCurrentNextBeamsDisabled(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (disabled: boolean) => {
    return ActionCreators.SetCurrentNextBeamsDisabled.create({ disabled, mapId });
  };
}

export function setMapZoomLatLng(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (latLng: google.maps.LatLng | google.maps.LatLngLiteral | undefined) => {
    return ActionCreators.SetZoomLatLng.create({ latLng, mapId });
  };
}
export function setSavedZoomSettings(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (center: google.maps.LatLng, zoom: number) => {
    return ActionCreators.SetSavedZoomSettings.create({ center, zoom, mapId });
  };
}
export function clearSavedZoomSettings(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return () => {
    return ActionCreators.SetSavedZoomSettings.create({ mapId });
  };
}

export function setMapHidden(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (hidden: boolean) => {
    return ActionCreators.SetMapHidden.create({ hidden, mapId });
  };
}

// GPS SA
export function setGpsSaMapLayersChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetGpsSaMapLayersChecked.create({ checked, mapId });
  };
}

export function setGpsSaMapLayersLowAccuracyChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetGpsSaMapLayersLowAccuracyChecked.create({ checked, mapId });
  };
}

export function setGpsSaMapLayersReducedAccuracyChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetGpsSaMapLayersReducedAccuracyChecked.create({ checked, mapId });
  };
}

export function setGpsSaMapLayersNormalAccuracyChecked(mapId = GOOGLE_MAP_INSTANCE_KEY) {
  return (checked: boolean) => {
    return ActionCreators.SetGpsSaMapLayersNormalAccuracyChecked.create({ checked, mapId });
  };
}

export const mapActionWrapper = (mapId = GOOGLE_MAP_INSTANCE_KEY) => ({
  setMapDimensions: setMapDimensions(mapId),
  clearMapDimensions: clearMapDimensions(mapId),
  setMapPosition: setMapPosition(mapId),
  clearMapPosition: clearMapPosition(mapId),
  setMissionPlanTakeoffMarker: setMissionPlanTakeoffMarker(mapId),
  setMissionPlanLandingMarker: setMissionPlanLandingMarker(mapId),
  setMissionPlanPathMarkers: setMissionPlanPathMarkers(mapId),
  showMapSettingsPanel: showMapSettingsPanel(mapId),
  hideMapSettingsPanel: hideMapSettingsPanel(mapId),
  toggleMapSettingsPanel: toggleMapSettingsPanel(mapId),
  setMapSettingsSatView: setMapSettingsSatView(mapId),
  setMapSettingsLabels: setMapSettingsLabels(mapId),
  setMapSettingsPrecipRadar: setMapSettingsPrecipRadar(mapId),
  setMapSettingsFTVMapSize: setMapSettingsFTVMapSize(mapId),
  showTogglePerformanceOutlookLegendPanel: showTogglePerformanceOutlookLegendPanel(mapId),
  hidePerformanceOutlookLegendPanel: hidePerformanceOutlookLegendPanel(mapId),
  togglePerformanceOutlookLegendPanel: togglePerformanceOutlookLegendPanel(mapId),
  showOverlaysPanel: showOverlaysPanel(mapId),
  hideOverlaysPanel: hideOverlaysPanel(mapId),
  toggleOverlaysPanel: toggleOverlaysPanel(mapId),
  toggleTailTagOverlaysPanel: toggleTailTagOverlaysPanel(mapId),
  showTailTagOverlaysPanel: showTailTagOverlaysPanel(mapId),
  hideTailTagOverlaysPanel: hideTailTagOverlaysPanel(mapId),
  toggleMapRulerTool: toggleMapRulerTool(mapId),
  showMapRulerTool: showMapRulerTool(mapId),
  hideMapRulerTool: hideMapRulerTool(mapId),
  appendLatLngToMapRulerTool: appendLatLngToMapRulerTool(mapId),
  prependLatLngToMapRulerTool: prependLatLngToMapRulerTool(mapId),
  updateLatLngInMapRulerTool: updateLatLngInMapRulerTool(mapId),
  removeLatLngInMapRulerTool: removeLatLngInMapRulerTool(mapId),
  clearLatLngsInMapRulerTool: clearLatLngsInMapRulerTool(mapId),
  setLegDataMapRulerTool: setLegDataMapRulerTool(mapId),
  clearLegDataMapRulerTool: clearLegDataMapRulerTool(mapId),
  setMarkersAndPolylinesMapRulerTool: setMarkersAndPolylinesMapRulerTool(mapId),
  setSelectedAntennaTypeId: setSelectedAntennaTypeId(mapId),
  setKuHnaZonesChecked: setKuHnaZonesChecked(mapId),
  setKaHnaZonesChecked: setKaHnaZonesChecked(mapId),
  setHcxHnaZonesChecked: setHcxHnaZonesChecked(mapId),
  setGxHnaZonesChecked: setGxHnaZonesChecked(mapId),
  switchToFlContours: switchToFlContours(mapId),
  switchToRlContours: switchToRlContours(mapId),
  enableHeatMaps: enableHeatMaps(mapId),
  disableHeatMaps: disableHeatMaps(mapId),
  enableGroundStations: enableGroundStations(mapId),
  disableGroundStations: disableGroundStations(mapId),
  addGroundStationsToMap: addGroundStationsToMap(mapId),
  clearGroundStationsOnMap: clearGroundStationsOnMap(mapId),
  addBeamToMap: addBeamToMap(mapId),
  removeBeamFromMap: removeBeamFromMap(mapId),
  clearBeamsOnMap: clearBeamsOnMap(mapId),
  updateCurrentOverlaysContext: updateCurrentOverlaysContext(mapId),
  setOpenedExpansionPanelId: setOpenedExpansionPanelId(mapId),
  setCurrentBeams: setCurrentBeams(mapId),
  setNextBeam: setNextBeam(mapId),
  setWeatherOverlayShown: setWeatherOverlayShown(mapId),
  clearCurrentOverlaysContextExceptTailTagOptions: clearCurrentOverlaysContextExceptTailTagOptions(mapId),
  setTailTagsShown: setTailTagsShown(mapId),
  setTerminalTagsSize: setTerminalTagsSize(mapId),
  clearMission: clearMission(mapId),
  showOverlaidFlightLeg: showOverlaidFlightLeg(mapId),
  toggleOverlaidFlightLegVisibility: toggleOverlaidFlightLegVisibility(mapId),
  setFlightPathBeamsDisabled: setFlightPathBeamsDisabled(mapId),
  setFlightPathBeamsChecked: setFlightPathBeamsChecked(mapId),
  setFlightPathWaypointsDisabled: setFlightPathWaypointsDisabled(mapId),
  setFlightPathWaypointsChecked: setFlightPathWaypointsChecked(mapId),
  overlaidFlightLegHidden: overlaidFlightLegHidden(mapId),
  setCurrentNextBeamsChecked: setCurrentNextBeamsChecked(mapId),
  setCurrentNextBeamsDisabled: setCurrentNextBeamsDisabled(mapId),
  setMapZoomLatLng: setMapZoomLatLng(mapId),
  setSavedZoomSettings: setSavedZoomSettings(mapId),
  clearSavedZoomSettings: clearSavedZoomSettings(mapId),
  setMapHidden: setMapHidden(mapId),
  setGpsSaMapLayersChecked: setGpsSaMapLayersChecked(mapId),
  setGpsSaMapLayersLowAccuracyChecked: setGpsSaMapLayersLowAccuracyChecked(mapId),
  setGpsSaMapLayersReducedAccuracyChecked: setGpsSaMapLayersReducedAccuracyChecked(mapId),
  setGpsSaMapLayersNormalAccuracyChecked: setGpsSaMapLayersNormalAccuracyChecked(mapId),
});
